import { TelemetryProtocolClient } from "../protocol/protocol-client";
import {
  ATTR_BASE_OFFSET,
  ATTR_TOTAL_BYTES,
  ATTR_WORD_SIZE,
  CHANNEL_COUNT,
  CHANNEL_NAME_SIZE,
  CHANNEL_NAME_TEXT_SIZE,
  CHANNEL_RECORD_SIZE,
  NAMES_BASE_OFFSET,
  NAMES_TOTAL_BYTES,
  RECORDS_BASE_OFFSET,
  RECORDS_TOTAL_BYTES,
  SPOT_CHANNEL_EXT_OFFSET,
  SPOT_CHANNEL_EXT_SIZE,
  SPOT_CHANNEL_EXT_TOTAL_BYTES,
  SPOT_LIST_GLOBALS_OFFSET,
  SPOT_LIST_GLOBALS_SIZE,
  SPOT_LIST_GLOBALS_VERSION,
  SPOT_RECEIVER_SETTINGS_MAGIC,
  SPOT_RECEIVER_SETTINGS_OFFSET,
  SPOT_RECEIVER_SETTINGS_SIZE,
  SPOT_RECEIVER_SETTINGS_VERSION,
  SPOT_LIST_NAME_SIZE,
  SPOT_LIST_NAME_UI_LEN,
  SPOT_LIST_NAMES_OFFSET,
  SPOT_LIST_NAMES_TOTAL_BYTES,
  SPOT_METADATA_COMPATIBILITY_VERSION,
  SPOT_CAP_FREQUENCY_OFFSET,
  SPOT_METADATA_MAGIC,
  SPOT_METADATA_OFFSET,
  SPOT_METADATA_SIZE,
  SPOT_METADATA_VERSION,
  VISIBLE_SCAN_LIST_COUNT,
  WRITE_BLOCK_SIZE,
  getDefaultVisibleScanListName,
  getChannelAttrOffset,
  getChannelNameTextRange,
  getSpotChannelExtOffset
} from "./radio-layout";
import {
  createDefaultRadioState,
  type ChannelModel,
  type ForceBandwidthMode,
  type RadioStateModel,
  type ScanTargetMode
} from "../types/radio-types";
import type { SerialConnectOptions, SerialTransport } from "../transport/serial-transport";
import { decodeChannelFromEeprom } from "./channel-decode";
import {
  sanitizeScanDwellTime,
  sanitizeTelemetryPeakTime
} from "./radio-sanitizers";
import {
  normalizeRadio,
  sanitizeBatteryCalibration,
  sanitizeTelemetryForceBandwidth,
  sanitizeFrequencyOffsetHz,
  sanitizeTelemetryFullScaleDbm,
  sanitizeTelemetryPulseThresholdDbm,
  sanitizeTelemetryRxGain
} from "./radio-normalization";

const SETTINGS_BLOCK_0E70 = 0xa000;
const SETTINGS_BLOCK_0E78 = 0xa008;
const SETTINGS_BLOCK_0E90 = 0xa0a8;
const SETTINGS_BLOCK_0EA0 = 0xa0b8;
const SETTINGS_BLOCK_0EA8 = 0xa0c0;
const SETTINGS_BLOCK_0F18 = 0xa130;
const SETTINGS_BLOCK_0F40 = 0xa150;
const SETTINGS_BLOCK_0EB0 = 0xa0c8;
const SETTINGS_BLOCK_0EC0 = 0xa0d8;
const SETTINGS_BLOCK_SIZE = 8;
const BATTERY_CALIBRATION_BLOCK = 0xb140;
const BATTERY_CALIBRATION_BLOCK_SIZE = 8;
const BATTERY_CALIBRATION_VALUE_OFFSET = 6;
const WELCOME_LINE_SIZE = 16;
const SCAN_LIST_CONFIG_VERSION = 1;
const SCAN_LIST_CONFIG_MAGIC = 0xa5;
const SPOT_CFG_0F18_NAV_ID_BIT = 0x01;
const SPOT_CFG_0F18_BPM_DISABLE_BIT = 0x02;
const CHANNEL_ATTR_FORMAT_MARKER = 0x1;
const TELEMETRY_FORCE_BANDWIDTH_MASK = 0x60;
const BACKLIGHT_UI_TO_FW = [0, 1, 2, 4, 12, 24, 48, 61] as const;

export interface ReadProgress {
  current: number;
  total: number;
  message: string;
}

export interface WriteProgress {
  current: number;
  total: number;
  phase: "write" | "verify";
  message: string;
}

export interface WriteBlock {
  offset: number;
  data: Uint8Array;
}

export interface WritePlan {
  changedChannelNumbers: number[];
  blocks: WriteBlock[];
  totalBytes: number;
}

export interface RadioSettingsWritePlan {
  blocks: WriteBlock[];
  totalBytes: number;
}

export type CompatibilityIssue =
  | "none"
  | "unverified"
  | "metadata_newer"
  | "metadata_mismatch"
  | "compatibility_newer"
  | "compatibility_older";

export interface RadioConnectionInfo {
  portLabel: string;
  firmwareVersion: string;
  compatibilityWarning: string;
  compatibilityIssue: CompatibilityIssue;
}

function cloneBytes(bytes: Uint8Array): Uint8Array {
  return new Uint8Array(bytes);
}

function compareBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function sanitizeChannelNameByte(value: number): number {
  return value >= 32 && value <= 126 ? value : 32;
}

function encodeSpotChannelRecord(channel: ChannelModel, record: Uint8Array): void {
  record.fill(0);
  const view = new DataView(record.buffer, record.byteOffset, record.byteLength);
  view.setUint32(0, channel.rxFrequency >>> 0, true);
  view.setUint32(4, 0, true);
  record[8] = 0;
  record[9] = 0;
  record[10] = 0;
  record[11] = 0x20;
  record[12] = 0x46;
  record[13] = 0;
  record[14] = channel.stepSetting & 0xff;
  record[15] = 0;
}

function encodeInactiveChannelRecord(record: Uint8Array): void {
  record.fill(0xff);
}

function applyChannelToRawBuffers(
  channel: ChannelModel,
  records: Uint8Array,
  names: Uint8Array,
  attrs: Uint8Array
): void {
  const index = channel.index;
  if (!Number.isInteger(index) || index < 0 || index >= CHANNEL_COUNT) {
    return;
  }

  const recordOffset = index * CHANNEL_RECORD_SIZE;
  const record = records.subarray(recordOffset, recordOffset + CHANNEL_RECORD_SIZE);
  if (channel.active) {
    encodeSpotChannelRecord(channel, record);
  } else {
    encodeInactiveChannelRecord(record);
  }

  const nameOffset = index * CHANNEL_NAME_SIZE;
  const nameBlock = names.subarray(nameOffset, nameOffset + CHANNEL_NAME_SIZE);
  nameBlock.fill(0, 0, CHANNEL_NAME_TEXT_SIZE);
  if (channel.active) {
    const name = channel.name.slice(0, CHANNEL_NAME_TEXT_SIZE);
    for (let i = 0; i < name.length; i += 1) {
      nameBlock[i] = sanitizeChannelNameByte(name.charCodeAt(i));
    }
  }

  // List membership lives exclusively in the SPOT channel-extension block.
  // Leave legacy name padding unchanged and never encode membership in attrs.
  const attrOffset = index * ATTR_WORD_SIZE;
  let attrWord = 0;
  if (channel.active) {
    attrWord =
      (channel.band & 0x07) |
      (CHANNEL_ATTR_FORMAT_MARKER << 13);
  } else {
    attrWord = 0x0007 | (CHANNEL_ATTR_FORMAT_MARKER << 13);
  }
  attrs[attrOffset] = attrWord & 0xff;
  attrs[attrOffset + 1] = (attrWord >> 8) & 0xff;
}

type SpotStorageMetadata = {
  compatibilityVersion: number;
  flags: number;
  metadataVersion: number;
  magic: number;
};

function decodeSpotStorageMetadata(data: Uint8Array): SpotStorageMetadata {
  return {
    compatibilityVersion: data[0] ?? 0xff,
    flags: data[1] ?? 0xff,
    metadataVersion: data[2] ?? 0xff,
    magic: data[3] ?? 0xff
  };
}

function getCompatibilityWarning(metadata: SpotStorageMetadata): {
  issue: CompatibilityIssue;
  warning: string;
} {
  if (metadata.magic !== SPOT_METADATA_MAGIC) {
    return {
      issue: "unverified",
      warning: "Receiver compatibility could not be verified. Reads may work, but writes should be used with caution."
    };
  }
  if (metadata.metadataVersion > SPOT_METADATA_VERSION) {
    return {
      issue: "metadata_newer",
      warning: "Receiver storage metadata is newer than this version of SPOT Editor. Reads may work, but writes could be unsafe."
    };
  }
  if (metadata.metadataVersion !== SPOT_METADATA_VERSION) {
    return {
      issue: "metadata_mismatch",
      warning: "Receiver storage metadata could not be verified. Reads may work, but writes should be used with caution."
    };
  }
  if (metadata.compatibilityVersion > SPOT_METADATA_COMPATIBILITY_VERSION) {
    return {
      issue: "compatibility_newer",
      warning: "Receiver compatibility is newer than this version of SPOT Editor. Reads may work, but writes could be unsafe."
    };
  }
  if (metadata.compatibilityVersion < SPOT_METADATA_COMPATIBILITY_VERSION) {
    return {
      issue: "compatibility_older",
      warning: "Receiver compatibility is older than this version of SPOT Editor. Save its configuration before upgrading firmware; the upgrade requires reprogramming the receiver."
    };
  }
  return {
    issue: "none",
    warning: ""
  };
}

function decodeZeroTerminatedAscii(data: Uint8Array): string {
  const slice = data.slice(0, WELCOME_LINE_SIZE);
  let end = slice.indexOf(0);
  if (end < 0) {
    end = slice.length;
  }
  return new TextDecoder().decode(slice.slice(0, end)).replace(/\s+$/u, "");
}

function encodeWelcomeLine(text: string): Uint8Array {
  const out = new Uint8Array(WELCOME_LINE_SIZE);
  const cleaned = text.replace(/[^\x20-\x7E]/g, "").slice(0, WELCOME_LINE_SIZE - 1);
  const bytes = new TextEncoder().encode(cleaned);
  out.set(bytes.slice(0, WELCOME_LINE_SIZE - 1));
  return out;
}

function decodeTelemetryForceBandwidth(raw: number): ForceBandwidthMode {
  switch (raw & TELEMETRY_FORCE_BANDWIDTH_MASK) {
    case 0x20:
      return "wide";
    case 0x40:
      return "narrow";
    case 0x60:
      return "narrower";
    case 0x00:
    default:
      return "dont_force";
  }
}

function encodeTelemetryForceBandwidth(mode: ForceBandwidthMode): number {
  switch (mode) {
    case "dont_force":
      return 0x00;
    case "wide":
      return 0x20;
    case "narrow":
      return 0x40;
    case "narrower":
      return 0x60;
    default:
      return 0x00;
  }
}

function decodeBacklightTime(raw: number, fallback: number): number {
  if (raw === 61) {
    return 7;
  }
  if (raw <= 0) {
    return 0;
  }
  const targetSeconds = raw * 5;
  const uiSeconds = [0, 5, 10, 20, 60, 120, 240];
  let bestUi = 1;
  let bestDiff = Number.MAX_SAFE_INTEGER;
  for (let ui = 1; ui <= 6; ui += 1) {
    const diff = Math.abs(uiSeconds[ui] - targetSeconds);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestUi = ui;
    }
  }
  return bestUi || fallback;
}

function encodeBacklightTime(uiValue: number): number {
  if (!Number.isFinite(uiValue)) {
    return BACKLIGHT_UI_TO_FW[3];
  }
  const clamped = Math.max(0, Math.min(7, Math.round(uiValue)));
  return BACKLIGHT_UI_TO_FW[clamped];
}

function decodePowerOnDisplayMode(
  value: number
): RadioStateModel["activeOptions"]["display"]["power_on_display_mode"] {
  switch (value) {
    case 0:
      return "message";
    case 1:
      return "sound";
    case 2:
      return "message";
    case 3:
      return "voltage";
    case 4:
      return "logo";
    case 5:
      return "none";
    default:
      return "message";
  }
}

function encodePowerOnDisplayMode(
  mode: RadioStateModel["activeOptions"]["display"]["power_on_display_mode"]
): number {
  switch (mode) {
    case "sound":
      return 1;
    case "message":
      return 2;
    case "voltage":
      return 3;
    case "logo":
      return 4;
    case "none":
      return 5;
    default:
      return 2;
  }
}

function clampScanMask(mask: number): number {
  return mask & 0xffff;
}

function parseScanTargetMode(rawValue: number): ScanTargetMode {
  return rawValue === 0 ? "lists" : "all";
}

function encodeScanTargetMode(value: ScanTargetMode): number {
  return value === "lists" ? 0 : 1;
}

function decodeScanListName(raw: Uint8Array, index: number): string {
  let end = 0;
  for (let i = 0; i < SPOT_LIST_NAME_UI_LEN; i += 1) {
    const value = raw[i];
    if (value < 32 || value > 126) {
      break;
    }
    end = i + 1;
  }
  const text = new TextDecoder().decode(raw.slice(0, end)).replace(/\s+$/u, "");
  return text || `L${index + 1}`;
}

function encodeScanListName(name: string): Uint8Array {
  const out = new Uint8Array(SPOT_LIST_NAME_SIZE);
  const cleaned = name.replace(/[^\x20-\x7E]/g, "").slice(0, SPOT_LIST_NAME_UI_LEN).trim();
  const bytes = new TextEncoder().encode(cleaned);
  out.fill(0x20);
  out.set(bytes.slice(0, SPOT_LIST_NAME_UI_LEN));
  return out;
}

function decodeScanListNames(storage: Uint8Array): string[] {
  const names: string[] = [];
  for (let i = 0; i < VISIBLE_SCAN_LIST_COUNT; i += 1) {
    const start = i * SPOT_LIST_NAME_SIZE;
    names.push(decodeScanListName(storage.subarray(start, start + SPOT_LIST_NAME_SIZE), i));
  }
  return names;
}

function applyScanListNames(storage: Uint8Array, names: string[]): void {
  for (let i = 0; i < VISIBLE_SCAN_LIST_COUNT; i += 1) {
    storage.set(encodeScanListName(names[i] || getDefaultVisibleScanListName(i)), i * SPOT_LIST_NAME_SIZE);
  }
}

type SpotListGlobals = {
  activeMask: number;
  defaultList: number;
  currentList: number;
  version: number;
};

function decodeSpotListGlobals(raw: Uint8Array): SpotListGlobals {
  const view = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  return {
    activeMask: view.getUint32(0, true),
    defaultList: raw[4] ?? 0xff,
    currentList: raw[5] ?? 0xff,
    version: raw[6] ?? 0xff
  };
}

function isSpotListGlobalsValid(globals: SpotListGlobals): boolean {
  return globals.version === SPOT_LIST_GLOBALS_VERSION;
}

function isSpotReceiverSettingsValid(settings: Uint8Array): boolean {
  return settings.length === SPOT_RECEIVER_SETTINGS_SIZE &&
    settings[1] === SPOT_RECEIVER_SETTINGS_VERSION &&
    settings[2] === SPOT_RECEIVER_SETTINGS_MAGIC;
}

type RadioRawBlocks = {
  metadata: SpotStorageMetadata;
  state0e70: Uint8Array;
  state0e78: Uint8Array;
  state0e90: Uint8Array;
  state0ea0: Uint8Array;
  state0ea8: Uint8Array;
  state0f18: Uint8Array;
  state0f40: Uint8Array;
  batteryCalibrationBlock: Uint8Array;
  welcome1: Uint8Array;
  welcome2: Uint8Array;
  scanListNamesBlock: Uint8Array;
  scanListGlobalsBlock: Uint8Array;
  receiverSettingsBlock: Uint8Array;
};

function decodeRadioSettingsFromRawBlocks(raw: RadioRawBlocks): RadioStateModel {
  const defaults = createDefaultRadioState();
  const radio = createDefaultRadioState();
  const isCurrentScanCfg = raw.state0f18[3] === SCAN_LIST_CONFIG_MAGIC && raw.state0f18[4] === SCAN_LIST_CONFIG_VERSION;
  let targetMode: ScanTargetMode = "all";
  let activeMask = 0;
  const globals = decodeSpotListGlobals(raw.scanListGlobalsBlock);
  if (isCurrentScanCfg) {
    targetMode = parseScanTargetMode(raw.state0f18[0]);
    activeMask = clampScanMask(raw.state0f18[1] | (raw.state0f18[2] << 8));
  } else if (raw.state0f18[7] === SCAN_LIST_CONFIG_MAGIC && raw.state0f18[6] === 1) {
    if (raw.state0f18[0] < 5) {
      targetMode = "lists";
      activeMask = 1 << raw.state0f18[0];
    }
  } else if (raw.state0f18[0] < 2) {
    targetMode = "lists";
    activeMask = 1 << raw.state0f18[0];
  }
  if (isSpotListGlobalsValid(globals)) {
    activeMask = clampScanMask(globals.activeMask);
  }
  radio.activeOptions.scan.active_list_mask = targetMode === "all" ? 0 : clampScanMask(activeMask);
  radio.activeOptions.scan.list_names = decodeScanListNames(raw.scanListNamesBlock);
  radio.activeOptions.scan.resume_mode = raw.state0e90[5] === 0 || raw.state0e90[5] === 2 ? "stop" : "dwell";
  radio.activeOptions.scan.dwell_time_seconds = sanitizeScanDwellTime(
    raw.state0ea8[6] >= 1 && raw.state0ea8[6] <= 30 ? raw.state0ea8[6] : defaults.activeOptions.scan.dwell_time_seconds
  );
  radio.activeOptions.telemetry.peak_time = sanitizeTelemetryPeakTime(
    raw.state0ea8[5] <= 118 ? raw.state0ea8[5] : defaults.activeOptions.telemetry.peak_time
  );
  radio.activeOptions.bpm_control =
    raw.state0f18[7] === 0xff ? true : ((raw.state0f18[7] & SPOT_CFG_0F18_BPM_DISABLE_BIT) === 0);
  radio.activeOptions.display_strength =
    isSpotReceiverSettingsValid(raw.receiverSettingsBlock) && raw.receiverSettingsBlock[0] === 1;
  radio.activeOptions.receiver.squelch_level =
    raw.state0e70[1] <= 9 ? raw.state0e70[1] : defaults.activeOptions.receiver.squelch_level;
  radio.activeOptions.receiver.rf_gain =
    (raw.state0ea8[7] & 0x1f) <= 24
      ? (raw.state0ea8[7] & 0x1f)
      : defaults.activeOptions.receiver.rf_gain;
  radio.activeOptions.developer.force_bandwidth = decodeTelemetryForceBandwidth(raw.state0ea8[7]);
  radio.activeOptions.receiver.navigation_mode =
    raw.state0f18[7] === 0xff || (raw.state0f18[7] & SPOT_CFG_0F18_NAV_ID_BIT) === 0 ? "channel" : "id";
  radio.activeOptions.display.power_on_display_mode = decodePowerOnDisplayMode(raw.state0e90[7]);
  radio.activeOptions.display.backlight_time = decodeBacklightTime(
    raw.state0e78[5],
    defaults.activeOptions.display.backlight_time
  );
  radio.activeOptions.beep_control = (raw.state0e90[0] & 0x01) !== 0;
  radio.activeOptions.battery_type = raw.state0ea8[4] <= 4 ? raw.state0ea8[4] : defaults.activeOptions.battery_type;
  radio.activeOptions.developer.pulse_threshold_dbm = sanitizeTelemetryPulseThresholdDbm((raw.state0f40[1] << 24) >> 24);
  radio.activeOptions.developer.full_scale_dbm = sanitizeTelemetryFullScaleDbm((raw.state0f40[2] << 24) >> 24);
  radio.activeOptions.developer.frequency_offset_hz = sanitizeFrequencyOffsetHz(
    new DataView(raw.state0f40.buffer, raw.state0f40.byteOffset, raw.state0f40.byteLength).getInt16(3, true)
  );
  radio.activeOptions.developer.battery_calibration = sanitizeBatteryCalibration(
    new DataView(
      raw.batteryCalibrationBlock.buffer,
      raw.batteryCalibrationBlock.byteOffset,
      raw.batteryCalibrationBlock.byteLength
    ).getUint16(BATTERY_CALIBRATION_VALUE_OFFSET, true)
  );
  radio.activeOptions.developer.present = {
    squelch_level: true,
    pulse_threshold_dbm: true,
    full_scale_dbm: true,
    frequency_offset_hz: (raw.metadata.flags & SPOT_CAP_FREQUENCY_OFFSET) !== 0,
    force_bandwidth: true,
    battery_calibration: true
  };
  radio.activeOptions.boot.line1 = decodeZeroTerminatedAscii(raw.welcome1);
  radio.activeOptions.boot.line2 = decodeZeroTerminatedAscii(raw.welcome2);
  radio.activeOptions.keys.flashlight_enabled = (((raw.state0e70[4] >> 2) & 0x0f) !== 0);
  radio.activeOptions.keys.arrow_orientation = (raw.state0e70[4] & 0x40) !== 0 ? "up_down" : "left_right";
  return normalizeRadio(radio).radio;
}

function buildRadioSettingsWritePlanFromRawBlocks(raw: RadioRawBlocks, desired: RadioStateModel): RadioSettingsWritePlan {
  const normalizedDesired = normalizeRadio(desired).radio;
  const next0e70 = cloneBytes(raw.state0e70);
  const next0e78 = cloneBytes(raw.state0e78);
  const next0e90 = cloneBytes(raw.state0e90);
  const next0ea0 = cloneBytes(raw.state0ea0);
  const next0ea8 = cloneBytes(raw.state0ea8);
  const next0f18 = cloneBytes(raw.state0f18);
  const next0f40 = cloneBytes(raw.state0f40);
  const nextBatteryCalibrationBlock = cloneBytes(raw.batteryCalibrationBlock);
  const nextWelcome1 = encodeWelcomeLine(normalizedDesired.activeOptions.boot.line1);
  const nextWelcome2 = encodeWelcomeLine(normalizedDesired.activeOptions.boot.line2);
  const nextScanListNamesBlock = cloneBytes(raw.scanListNamesBlock);
  const nextScanListGlobalsBlock = cloneBytes(raw.scanListGlobalsBlock);
  const nextReceiverSettingsBlock = cloneBytes(raw.receiverSettingsBlock);

  if (!isSpotReceiverSettingsValid(nextReceiverSettingsBlock)) {
    nextReceiverSettingsBlock.fill(0xff);
    nextReceiverSettingsBlock[1] = SPOT_RECEIVER_SETTINGS_VERSION;
    nextReceiverSettingsBlock[2] = SPOT_RECEIVER_SETTINGS_MAGIC;
  }
  nextReceiverSettingsBlock[0] = normalizedDesired.activeOptions.display_strength ? 1 : 0;

  if (normalizedDesired.activeOptions.developer.present.squelch_level) {
    next0e70[1] = normalizedDesired.activeOptions.receiver.squelch_level;
  }
  next0ea8[7] =
    normalizedDesired.activeOptions.receiver.rf_gain |
    encodeTelemetryForceBandwidth(
      normalizedDesired.activeOptions.developer.present.force_bandwidth
        ? normalizedDesired.activeOptions.developer.force_bandwidth
        : decodeTelemetryForceBandwidth(raw.state0ea8[7])
    );
  next0e78[5] = encodeBacklightTime(normalizedDesired.activeOptions.display.backlight_time);
  next0e90[5] = normalizedDesired.activeOptions.scan.resume_mode === "stop" ? 0 : 1;
  next0e90[7] = encodePowerOnDisplayMode(normalizedDesired.activeOptions.display.power_on_display_mode);
  next0ea8[4] = normalizedDesired.activeOptions.battery_type;
  next0ea8[5] = normalizedDesired.activeOptions.telemetry.peak_time;
  next0ea8[6] = normalizedDesired.activeOptions.scan.dwell_time_seconds;
  const setKey = normalizedDesired.activeOptions.keys.flashlight_enabled ? 1 : 0;
  next0e70[4] = (next0e70[4] & 0xc3) | ((setKey & 0x0f) << 2);
  if (normalizedDesired.activeOptions.keys.arrow_orientation === "up_down") {
    next0e70[4] |= 0x40;
  } else {
    next0e70[4] &= ~0x40;
  }
  const activeMask = clampScanMask(normalizedDesired.activeOptions.scan.active_list_mask);
  const targetMode: ScanTargetMode = activeMask === 0 ? "all" : "lists";
  const storedMask = targetMode === "all" ? 0 : activeMask;
  next0f18[0] = encodeScanTargetMode(targetMode);
  next0f18[1] = storedMask & 0xff;
  next0f18[2] = (storedMask >> 8) & 0xff;
  next0f18[3] = SCAN_LIST_CONFIG_MAGIC;
  next0f18[4] = SCAN_LIST_CONFIG_VERSION;
  next0f18[7] = normalizedDesired.activeOptions.receiver.navigation_mode === "id" ? SPOT_CFG_0F18_NAV_ID_BIT : 0x00;
  if (!normalizedDesired.activeOptions.bpm_control) {
    next0f18[7] |= SPOT_CFG_0F18_BPM_DISABLE_BIT;
  }
  next0e90[0] = (next0e90[0] & 0xfe) | (normalizedDesired.activeOptions.beep_control ? 0x01 : 0x00);
  if (normalizedDesired.activeOptions.developer.present.pulse_threshold_dbm) {
    next0f40[1] = normalizedDesired.activeOptions.developer.pulse_threshold_dbm & 0xff;
  }
  if (normalizedDesired.activeOptions.developer.present.full_scale_dbm) {
    next0f40[2] = normalizedDesired.activeOptions.developer.full_scale_dbm & 0xff;
  }
  if (
    (raw.metadata.flags & SPOT_CAP_FREQUENCY_OFFSET) !== 0 &&
    normalizedDesired.activeOptions.developer.present.frequency_offset_hz
  ) {
    new DataView(next0f40.buffer, next0f40.byteOffset, next0f40.byteLength).setInt16(
      3,
      sanitizeFrequencyOffsetHz(normalizedDesired.activeOptions.developer.frequency_offset_hz),
      true
    );
  }
  if (normalizedDesired.activeOptions.developer.present.battery_calibration) {
    new DataView(
      nextBatteryCalibrationBlock.buffer,
      nextBatteryCalibrationBlock.byteOffset,
      nextBatteryCalibrationBlock.byteLength
    ).setUint16(
      BATTERY_CALIBRATION_VALUE_OFFSET,
      normalizedDesired.activeOptions.developer.battery_calibration,
      true
    );
  }
  applyScanListNames(nextScanListNamesBlock, normalizedDesired.activeOptions.scan.list_names);
  const globalsView = new DataView(
    nextScanListGlobalsBlock.buffer,
    nextScanListGlobalsBlock.byteOffset,
    nextScanListGlobalsBlock.byteLength
  );
  globalsView.setUint32(0, storedMask >>> 0, true);
  nextScanListGlobalsBlock[6] = SPOT_LIST_GLOBALS_VERSION;
  nextScanListGlobalsBlock[7] = 0;

  const blocks: WriteBlock[] = [];
  const pushIfChanged = (offset: number, current: Uint8Array, next: Uint8Array): void => {
    if (!compareBytes(current, next)) {
      for (let i = 0; i < next.length; i += WRITE_BLOCK_SIZE) {
        blocks.push({ offset: offset + i, data: cloneBytes(next.subarray(i, i + WRITE_BLOCK_SIZE)) });
      }
    }
  };

  pushIfChanged(SETTINGS_BLOCK_0E70, raw.state0e70, next0e70);
  pushIfChanged(SETTINGS_BLOCK_0E78, raw.state0e78, next0e78);
  pushIfChanged(SETTINGS_BLOCK_0E90, raw.state0e90, next0e90);
  pushIfChanged(SETTINGS_BLOCK_0EA0, raw.state0ea0, next0ea0);
  pushIfChanged(SETTINGS_BLOCK_0EA8, raw.state0ea8, next0ea8);
  pushIfChanged(SETTINGS_BLOCK_0F18, raw.state0f18, next0f18);
  pushIfChanged(SETTINGS_BLOCK_0F40, raw.state0f40, next0f40);
  pushIfChanged(BATTERY_CALIBRATION_BLOCK, raw.batteryCalibrationBlock, nextBatteryCalibrationBlock);
  pushIfChanged(SETTINGS_BLOCK_0EB0, raw.welcome1, nextWelcome1);
  pushIfChanged(SETTINGS_BLOCK_0EC0, raw.welcome2, nextWelcome2);
  pushIfChanged(SPOT_LIST_NAMES_OFFSET, raw.scanListNamesBlock, nextScanListNamesBlock);
  pushIfChanged(SPOT_LIST_GLOBALS_OFFSET, raw.scanListGlobalsBlock, nextScanListGlobalsBlock);
  pushIfChanged(SPOT_RECEIVER_SETTINGS_OFFSET, raw.receiverSettingsBlock, nextReceiverSettingsBlock);

  blocks.sort((a, b) => a.offset - b.offset);
  return { blocks, totalBytes: blocks.length * WRITE_BLOCK_SIZE };
}

function hasRegionDiff(currentRegion: Uint8Array, nextRegion: Uint8Array, offsetInRegion: number, length: number): boolean {
  for (let i = 0; i < length; i += 1) {
    if (currentRegion[offsetInRegion + i] !== nextRegion[offsetInRegion + i]) {
      return true;
    }
  }
  return false;
}

function hasAbsoluteDiff(
  currentRegion: Uint8Array,
  nextRegion: Uint8Array,
  absoluteOffset: number,
  baseOffset: number,
  length: number
): boolean {
  const offsetInRegion = absoluteOffset - baseOffset;
  return hasRegionDiff(currentRegion, nextRegion, offsetInRegion, length);
}

function blockFromRegion(baseOffset: number, region: Uint8Array, blockOffset: number): WriteBlock {
  const blockInRegion = blockOffset - baseOffset;
  return {
    offset: blockOffset,
    data: cloneBytes(region.subarray(blockInRegion, blockInRegion + WRITE_BLOCK_SIZE))
  };
}

function buildWritePlanFromRawRegions(
  currentRecords: Uint8Array,
  currentNames: Uint8Array,
  currentAttrs: Uint8Array,
  currentChannelExt: Uint8Array,
  desiredRecords: Uint8Array,
  desiredNames: Uint8Array,
  desiredAttrs: Uint8Array,
  desiredChannelExt: Uint8Array
): WritePlan {
  const changedChannelNumbers: number[] = [];
  const changedBlockOffsets = new Set<number>();

  for (let index = 0; index < CHANNEL_COUNT; index += 1) {
    const recordOffset = index * CHANNEL_RECORD_SIZE;
    const recordDiff = hasRegionDiff(currentRecords, desiredRecords, recordOffset, CHANNEL_RECORD_SIZE);
    const [nameTextStart, nameTextEnd] = getChannelNameTextRange(index);
    const nameTextDiff = hasAbsoluteDiff(currentNames, desiredNames, nameTextStart, NAMES_BASE_OFFSET, nameTextEnd - nameTextStart);
    const attrOffset = getChannelAttrOffset(index) - ATTR_BASE_OFFSET;
    const currentAttrWord = currentAttrs[attrOffset] | (currentAttrs[attrOffset + 1] << 8);
    const desiredAttrWord = desiredAttrs[attrOffset] | (desiredAttrs[attrOffset + 1] << 8);
    const attrDiff =
      currentAttrWord !== desiredAttrWord;
    const extOffset = getSpotChannelExtOffset(index) - SPOT_CHANNEL_EXT_OFFSET;
    const extDiff = hasRegionDiff(currentChannelExt, desiredChannelExt, extOffset, SPOT_CHANNEL_EXT_SIZE);

    if (recordDiff || nameTextDiff || attrDiff || extDiff) {
      changedChannelNumbers.push(index + 1);
    }

    if (recordDiff) {
      for (let byte = 0; byte < CHANNEL_RECORD_SIZE; byte += 1) {
        if (currentRecords[recordOffset + byte] !== desiredRecords[recordOffset + byte]) {
          const absoluteByteOffset = RECORDS_BASE_OFFSET + recordOffset + byte;
          changedBlockOffsets.add(absoluteByteOffset & ~(WRITE_BLOCK_SIZE - 1));
        }
      }
    }

    if (nameTextDiff) {
      for (let absoluteByteOffset = nameTextStart; absoluteByteOffset < nameTextEnd; absoluteByteOffset += 1) {
        const byteOffset = absoluteByteOffset - NAMES_BASE_OFFSET;
        if (currentNames[byteOffset] !== desiredNames[byteOffset]) {
          changedBlockOffsets.add(absoluteByteOffset & ~(WRITE_BLOCK_SIZE - 1));
        }
      }
    }

    if (attrDiff) {
      const absoluteByteOffset = getChannelAttrOffset(index);
      changedBlockOffsets.add(absoluteByteOffset & ~(WRITE_BLOCK_SIZE - 1));
    }
    if (extDiff) {
      for (let byte = 0; byte < SPOT_CHANNEL_EXT_SIZE; byte += 1) {
        if (currentChannelExt[extOffset + byte] !== desiredChannelExt[extOffset + byte]) {
          const absoluteByteOffset = SPOT_CHANNEL_EXT_OFFSET + extOffset + byte;
          changedBlockOffsets.add(absoluteByteOffset & ~(WRITE_BLOCK_SIZE - 1));
        }
      }
    }
  }

  const sortedOffsets = [...changedBlockOffsets].sort((a, b) => a - b);
  const blocks: WriteBlock[] = sortedOffsets.map((offset) => {
    if (offset >= RECORDS_BASE_OFFSET && offset + WRITE_BLOCK_SIZE <= RECORDS_BASE_OFFSET + RECORDS_TOTAL_BYTES) {
      return blockFromRegion(RECORDS_BASE_OFFSET, desiredRecords, offset);
    }
    if (offset >= NAMES_BASE_OFFSET && offset + WRITE_BLOCK_SIZE <= NAMES_BASE_OFFSET + NAMES_TOTAL_BYTES) {
      return blockFromRegion(NAMES_BASE_OFFSET, desiredNames, offset);
    }
    if (offset >= ATTR_BASE_OFFSET && offset + WRITE_BLOCK_SIZE <= ATTR_BASE_OFFSET + ATTR_TOTAL_BYTES) {
      return blockFromRegion(ATTR_BASE_OFFSET, desiredAttrs, offset);
    }
    if (offset >= SPOT_CHANNEL_EXT_OFFSET && offset + WRITE_BLOCK_SIZE <= SPOT_CHANNEL_EXT_OFFSET + SPOT_CHANNEL_EXT_TOTAL_BYTES) {
      return blockFromRegion(SPOT_CHANNEL_EXT_OFFSET, desiredChannelExt, offset);
    }
    throw new Error(`Write plan produced out-of-range block at 0x${offset.toString(16)}`);
  });

  return { changedChannelNumbers, blocks, totalBytes: blocks.length * WRITE_BLOCK_SIZE };
}

export class SpotRadioCore {
  private readonly protocol: TelemetryProtocolClient;
  private lastKnownFirmwareVersion: string | null = null;

  constructor(
    private readonly transport: SerialTransport,
    protocol?: TelemetryProtocolClient
  ) {
    this.protocol = protocol ?? new TelemetryProtocolClient(this.transport);
  }

  setDisconnectHandler(handler: ((reason: string) => void) | null): void {
    this.transport.setDisconnectHandler(handler);
  }

  async connect(portPath?: string, options?: SerialConnectOptions): Promise<RadioConnectionInfo> {
    const { portLabel } = await this.transport.connect(portPath, options);
    try {
      const session = await this.protocol.startSession();
      this.lastKnownFirmwareVersion = session.version || "unknown";
      let compatibilityWarning = "";
      let compatibilityIssue: CompatibilityIssue = "none";

      try {
        const metadataBytes = await this.protocol.readEeprom(SPOT_METADATA_OFFSET, SPOT_METADATA_SIZE);
        const metadata = decodeSpotStorageMetadata(metadataBytes);
        const compatibility = getCompatibilityWarning(metadata);
        compatibilityIssue = compatibility.issue;
        compatibilityWarning = compatibility.warning;
      } catch {
        compatibilityIssue = "unverified";
        compatibilityWarning =
          "Receiver compatibility could not be verified. Reads may work, but writes should be used with caution.";
      }

      return {
        portLabel,
        firmwareVersion: this.lastKnownFirmwareVersion,
        compatibilityWarning,
        compatibilityIssue
      };
    } catch (error) {
      await this.transport.disconnect().catch(() => {});
      this.lastKnownFirmwareVersion = null;
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    await this.transport.disconnect();
    this.lastKnownFirmwareVersion = null;
  }

  async probeRadio(): Promise<{ firmwareVersion: string }> {
    await this.protocol.probeAdc();
    return { firmwareVersion: this.lastKnownFirmwareVersion ?? "unknown" };
  }

  async restartRadio(): Promise<void> {
    await this.protocol.requestReset();
  }

  /** Reads the receiver's factory-programmed, immutable device identifier. */
  async readDeviceId(): Promise<string> {
    return this.protocol.readDeviceId();
  }

  private async readRadioSettingsRawBlocks(): Promise<RadioRawBlocks> {
    let metadata: SpotStorageMetadata;
    try {
      const metadataBytes = await this.protocol.readEeprom(SPOT_METADATA_OFFSET, SPOT_METADATA_SIZE);
      metadata = decodeSpotStorageMetadata(metadataBytes);
    } catch {
      metadata = {
        compatibilityVersion: 0xff,
        flags: 0,
        metadataVersion: 0xff,
        magic: 0xff
      };
    }
    const state0e70 = await this.protocol.readEeprom(SETTINGS_BLOCK_0E70, SETTINGS_BLOCK_SIZE);
    const state0e78 = await this.protocol.readEeprom(SETTINGS_BLOCK_0E78, SETTINGS_BLOCK_SIZE);
    const state0e90 = await this.protocol.readEeprom(SETTINGS_BLOCK_0E90, SETTINGS_BLOCK_SIZE);
    const state0ea0 = await this.protocol.readEeprom(SETTINGS_BLOCK_0EA0, SETTINGS_BLOCK_SIZE);
    const state0ea8 = await this.protocol.readEeprom(SETTINGS_BLOCK_0EA8, SETTINGS_BLOCK_SIZE);
    const state0f18 = await this.protocol.readEeprom(SETTINGS_BLOCK_0F18, SETTINGS_BLOCK_SIZE);
    const state0f40 = await this.protocol.readEeprom(SETTINGS_BLOCK_0F40, SETTINGS_BLOCK_SIZE);
    const batteryCalibrationBlock = await this.protocol.readEeprom(BATTERY_CALIBRATION_BLOCK, BATTERY_CALIBRATION_BLOCK_SIZE);
    const welcome1 = await this.protocol.readEeprom(SETTINGS_BLOCK_0EB0, WELCOME_LINE_SIZE);
    const welcome2 = await this.protocol.readEeprom(SETTINGS_BLOCK_0EC0, WELCOME_LINE_SIZE);
    const scanListNamesBlock = await this.protocol.readEeprom(SPOT_LIST_NAMES_OFFSET, SPOT_LIST_NAMES_TOTAL_BYTES);
    const scanListGlobalsBlock = await this.protocol.readEeprom(SPOT_LIST_GLOBALS_OFFSET, SPOT_LIST_GLOBALS_SIZE);
    const receiverSettingsBlock = await this.protocol.readEeprom(
      SPOT_RECEIVER_SETTINGS_OFFSET,
      SPOT_RECEIVER_SETTINGS_SIZE
    );
    return {
      metadata,
      state0e70,
      state0e78,
      state0e90,
      state0ea0,
      state0ea8,
      state0f18,
      state0f40,
      batteryCalibrationBlock,
      welcome1,
      welcome2,
      scanListNamesBlock,
      scanListGlobalsBlock,
      receiverSettingsBlock
    };
  }

  async readRadioSettings(): Promise<RadioStateModel> {
    return decodeRadioSettingsFromRawBlocks(await this.readRadioSettingsRawBlocks());
  }

  async prepareRadioSettingsWritePlan(radio: RadioStateModel): Promise<RadioSettingsWritePlan> {
    return buildRadioSettingsWritePlanFromRawBlocks(await this.readRadioSettingsRawBlocks(), radio);
  }

  async writeRadioSettings(
    radio: RadioStateModel,
    onProgress: (update: WriteProgress) => void,
    verifyAfterEachWrite = true
  ): Promise<RadioSettingsWritePlan> {
    const plan = await this.prepareRadioSettingsWritePlan(radio);
    const total = plan.blocks.length;
    if (total === 0) {
      onProgress({ current: 0, total: 0, phase: "write", message: "No radio setting changes detected" });
      return plan;
    }
    for (let i = 0; i < plan.blocks.length; i += 1) {
      const block = plan.blocks[i];
      await this.protocol.writeEeprom(block.offset, block.data, false);
      onProgress({
        current: i + 1,
        total,
        phase: "write",
        message: `Wrote settings block ${i + 1}/${total} at 0x${block.offset.toString(16).toUpperCase()}`
      });
      if (verifyAfterEachWrite) {
        const readback = await this.protocol.readEeprom(block.offset, WRITE_BLOCK_SIZE);
        if (!compareBytes(readback, block.data)) {
          throw new Error(`Settings verification mismatch at 0x${block.offset.toString(16).toUpperCase()}`);
        }
        onProgress({
          current: i + 1,
          total,
          phase: "verify",
          message: `Verified settings block ${i + 1}/${total} at 0x${block.offset.toString(16).toUpperCase()}`
        });
      }
    }
    return plan;
  }

  async readFromRadio(onProgress: (update: ReadProgress) => void): Promise<ChannelModel[]> {
    const total = CHANNEL_COUNT;
    const channels: ChannelModel[] = [];
    const channelExt = await this.protocol.readEeprom(SPOT_CHANNEL_EXT_OFFSET, SPOT_CHANNEL_EXT_TOTAL_BYTES);
    let currentAttrBlock = new Uint8Array(16);
    let currentAttrBlockBase = -1;

    for (let index = 0; index < total; index += 1) {
      onProgress({ current: index + 1, total, message: `Reading channel ${index + 1} / ${total}` });
      const recordOffset = index * 16;
      const nameOffset = NAMES_BASE_OFFSET + recordOffset;
      const attrBase = ATTR_BASE_OFFSET + ((index & ~7) * ATTR_WORD_SIZE);
      if (attrBase !== currentAttrBlockBase) {
        currentAttrBlock = new Uint8Array(await this.protocol.readEeprom(attrBase, 16));
        currentAttrBlockBase = attrBase;
      }
      const rawRecord = await this.protocol.readEeprom(recordOffset, 16);
      const rawName = await this.protocol.readEeprom(nameOffset, 16);
      const attrIndex = (index & 7) * ATTR_WORD_SIZE;
      const attribute = currentAttrBlock[attrIndex] | (currentAttrBlock[attrIndex + 1] << 8);
      const channel = decodeChannelFromEeprom(index, rawRecord, rawName, attribute);
      const extView = new DataView(
        channelExt.buffer,
        channelExt.byteOffset + (index * SPOT_CHANNEL_EXT_SIZE),
        SPOT_CHANNEL_EXT_SIZE
      );
      channel.scanlistMask = extView.getUint32(0, true) & 0xffff;
      channels.push(channel);
    }
    return channels;
  }

  async prepareWritePlan(channels: ChannelModel[]): Promise<WritePlan> {
    const currentRecords = await this.protocol.readEeprom(RECORDS_BASE_OFFSET, RECORDS_TOTAL_BYTES);
    const currentNames = await this.protocol.readEeprom(NAMES_BASE_OFFSET, NAMES_TOTAL_BYTES);
    const currentAttrs = await this.protocol.readEeprom(ATTR_BASE_OFFSET, ATTR_TOTAL_BYTES);
    const currentChannelExt = await this.protocol.readEeprom(SPOT_CHANNEL_EXT_OFFSET, SPOT_CHANNEL_EXT_TOTAL_BYTES);
    const desiredRecords = cloneBytes(currentRecords);
    const desiredNames = cloneBytes(currentNames);
    const desiredAttrs = cloneBytes(currentAttrs);
    const desiredChannelExt = cloneBytes(currentChannelExt);

    for (const channel of channels) {
      applyChannelToRawBuffers(channel, desiredRecords, desiredNames, desiredAttrs);
      if (channel.index >= 0 && channel.index < CHANNEL_COUNT) {
        new DataView(
          desiredChannelExt.buffer,
          desiredChannelExt.byteOffset + (channel.index * SPOT_CHANNEL_EXT_SIZE),
          SPOT_CHANNEL_EXT_SIZE
        ).setUint32(0, channel.active ? (channel.scanlistMask & 0xffff) : 0, true);
      }
    }

    return buildWritePlanFromRawRegions(
      currentRecords,
      currentNames,
      currentAttrs,
      currentChannelExt,
      desiredRecords,
      desiredNames,
      desiredAttrs,
      desiredChannelExt
    );
  }

  async writeToRadio(
    channels: ChannelModel[],
    onProgress: (update: WriteProgress) => void,
    verifyAfterEachWrite = true
  ): Promise<WritePlan> {
    const plan = await this.prepareWritePlan(channels);
    const total = plan.blocks.length;
    if (total === 0) {
      onProgress({ current: 0, total: 0, phase: "write", message: "No EEPROM changes detected" });
      return plan;
    }
    for (let i = 0; i < plan.blocks.length; i += 1) {
      const block = plan.blocks[i];
      await this.protocol.writeEeprom(block.offset, block.data, false);
      onProgress({
        current: i + 1,
        total,
        phase: "write",
        message: `Wrote block ${i + 1}/${total} at 0x${block.offset.toString(16).toUpperCase()}`
      });
      if (verifyAfterEachWrite) {
        const readback = await this.protocol.readEeprom(block.offset, WRITE_BLOCK_SIZE);
        if (!compareBytes(readback, block.data)) {
          throw new Error(`Verification mismatch at 0x${block.offset.toString(16).toUpperCase()}`);
        }
        onProgress({
          current: i + 1,
          total,
          phase: "verify",
          message: `Verified block ${i + 1}/${total} at 0x${block.offset.toString(16).toUpperCase()}`
        });
      }
    }
    return plan;
  }
}
